import { useToast } from '@app/core/notifications';
import { saveFileResponse } from '@app/helpers/file';
import { getApiV2Client, getAuditReportsGeneratePreviewReportPreviewUrl } from '@mi-front/api-v2';
import { Visibility } from '@mui/icons-material';
import { Button, Menu, MenuItem } from '@mui/material';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { ReportDefinition } from '../../../types';
import { useDefinitionContext } from '../../contexts/DefinitionContext';

export function PreviewButton() {
  const { $t } = useIntl();
  const { notifyError } = useToast();
  const { blueprint, languages } = useDefinitionContext();
  const { watch } = useFormContext<ReportDefinition>();
  const formData = watch();
  const [previewMenuAnchor, setPreviewMenuAnchor] = useState<null | HTMLElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const language = languages[0] || 'en';

  const handlePreviewClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setPreviewMenuAnchor(event.currentTarget);
  };

  const handlePreviewClose = () => {
    setPreviewMenuAnchor(null);
  };

  const handlePreviewSelect = async (format: 'xls' | 'csv') => {
    handlePreviewClose();
    setIsGenerating(true);

    try {
      const apiClient = getApiV2Client();
      const { url } = getAuditReportsGeneratePreviewReportPreviewUrl(blueprint.reportType);
      const previewUrl = apiClient.generateUrl(url);
      const res = await apiClient.request<ArrayBuffer>('POST', previewUrl, {
        params: {
          format,
          language,
          blueprintVersion: blueprint.version,
        },
        data: formData.data,
        responseType: 'arraybuffer' as const,
      });

      saveFileResponse(res);
    } catch (error) {
      notifyError(
        $t({
          id: 'audit.reportDefinitions.previewError',
          defaultMessage: 'Error al generar la vista previa',
        }),
      );
      console.error(error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <Button
        color="info"
        variant="outlined"
        size="large"
        onClick={handlePreviewClick}
        disabled={isGenerating}
        startIcon={<Visibility />}
      >
        {isGenerating
          ? $t({
              id: 'audit.preview.generating',
              defaultMessage: 'Generando...',
            })
          : $t({
              id: 'audit.preview.preview',
              defaultMessage: 'Vista Previa',
            })}
      </Button>
      <Menu
        anchorEl={previewMenuAnchor}
        open={Boolean(previewMenuAnchor)}
        onClose={handlePreviewClose}
      >
        <MenuItem onClick={() => handlePreviewSelect('xls')}>
          {$t({
            id: 'audit.preview.previewXls',
            defaultMessage: 'Vista previa XLS',
          })}
        </MenuItem>
        <MenuItem onClick={() => handlePreviewSelect('csv')}>
          {$t({
            id: 'audit.preview.previewCsv',
            defaultMessage: 'Vista previa CSV',
          })}
        </MenuItem>
      </Menu>
    </>
  );
}
